import React, { useCallback, useEffect, useRef } from 'react'
import { useRecoilCallback, useRecoilState } from 'recoil'
import { useIntl } from 'react-intl'
import { modalEmailMessagesState } from 'Hooks/useModalEmailMessages/useModalEmailMessages'

import Slider from 'rc-slider'
import 'rc-slider/assets/index.css'

import {
  Title,
  TITLE_VARIANT_M,
  TITLE_VARIANT_S,
  Panel,
  FormElement,
  InputText,
  Button,
  BUTTON_VARIANT_PRIMARY,
  Grid,
  GridCell,
  ThumbContainer,
  SpacingVertical,
  SPACING_8,
  SPACING_32,
  Thumb,
  Tooltip,
  TooltipUtils,
  Flex,
  FlexItem
} from 'mailup-web-components'
import styled from '@emotion/styled'

import ModalEmailMessages from 'Components/ModalEmailMessages/ModalEmailMessages'
import BoxThumbnail from './BoxThumbnail'

import emptyPreviewTemplate from 'mailup-web-components/resources/mailup-web-app/img/svg/empty-preview-template.svg'
import './BoxTestInfo.scss'

import { abTestState } from '../../recoil/state/abtest'
import { getEmailSelectedMessageCallback } from '../../recoil/callbacks/callbacks'
import ModalMessageInUse from '../ModalMessageInUse/ModalMessageInUse'


const handleRender = (node, handleProps) => {
  const el = useRef(null)

  useEffect(() => {
    if (el && el.current) {
      const handleElement = el.current.querySelector('.rc-slider-handle')

      handleElement.setAttribute('data-for', 'handle-tooltip')
      handleElement.setAttribute('data-tip', `${handleProps.value}%`)

      TooltipUtils.show(handleElement)
    }
  }, [handleProps.dragging, handleProps.value])

  return <div ref={el}>{node}</div>
}

const AddNewMessageBox = styled.div`
  background: ${props =>
    props.enabled && !props.isSelectable
      ? `url(${emptyPreviewTemplate}) 50% no-repeat #f5f5f5`
      : ''};
  background-color: ${props => (props.isSelectable ? '#f5f5f5' : '')};
  height: calc(100% + 57px);
  width: 100%;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  text-align: center;
`

const BoxTestInfo = () => {
  const intl = useIntl()
  const [{ step1 }, setAbTestState] = useRecoilState(abTestState)
  const [modalEmailState, setModalEmailState] = useRecoilState(
    modalEmailMessagesState
  )

  const getEmailSelectedMessage = useRecoilCallback(
    getEmailSelectedMessageCallback,
    []
  )

  const onChangeName = e => {
    setAbTestState(prev => ({
      ...prev,
      step1: {
        ...prev.step1,
        name: e.target.value,
        nameIsDiry: true,
        step1IsDirty: true
      }
    }))
  }

  const onChangePercTest = val => {
    if (val >= 5 && val <= 100) {
      setAbTestState(prev => ({
        ...prev,
        step1: {
          ...prev.step1,
          percTest: val,
          step1IsDirty: true,
          tipoTest: val === 100 ? '' : prev.step1.tipoTest
        }
      }))
    }
  }

  const selectMessage = () => {
    setModalEmailState(prev => ({
      ...prev,
      show: true,
      selectedMessages: step1.abTestMessages,
      selectedItem: undefined
    }))
  }
  const handleEmailConfirm = useCallback(async (e, value, droppableId) => {
    const ret = await getEmailSelectedMessage(
      value,
      step1.abTestMessages,
      droppableId
    )

    const newMessage = setSelectedMessageObj(value)

    if (ret) {
      setAbTestState(prev => ({
        ...prev,
        step1: {
          ...prev.step1,
          step1IsDirty: true,
          abTestMessages: [...prev.step1.abTestMessages, newMessage]
        }
      }))
      setModalEmailState(prev => ({
        ...prev,
        show: false,
        selectedItem: undefined
      }))
    }
  })

  const setSelectedMessageObj = value => {
    return {
      IdMessaggio: value.Id,
      IdInvio: 0,
      TipoInvio: 1,
      IdSchedule: 0,
      Queued: 0,
      ToBeQueued: 0,
      Subject: value.Subject,
      Note: value.Note,
      HasThumbnail: value.HasThumbnail,
      LastUpdated: value.LastUpdated
    }
  }
  // chiude la finestra di selezione email
  const handleEmailCancel = () => {
    setModalEmailState(prev => ({
      ...prev,
      show: false,
      droppableId: ''
    }))
  }

  return (
    <>
      <ModalEmailMessages
        handleCancel={handleEmailCancel}
        handleConfirm={handleEmailConfirm}
      />
      <ModalMessageInUse />

      <Title variant={TITLE_VARIANT_M}>
        {intl.formatMessage({ id: 'Load.ABTestWizard.TitleTest' })}
      </Title>
      <SpacingVertical variant={SPACING_8} />
      <Panel collapsible={false}>
        <FormElement
          labelFor='input-name'
          required
          label={intl.formatMessage({ id: 'Load.ABTestWizard.NameTest' })}
          feedback={
            step1.nameIsDiry && !step1.name
              ? intl.formatMessage({ id: 'Load.MailupCommon.Required' })
              : null
          }
        >
          <InputText
            id='input-name'
            placeholder={intl.formatMessage({
              id: 'Load.ABTestWizard.NameTestPlaceholder'
            })}
            onChange={onChangeName}
            value={step1.name}
            data-mu-test='input-name'
            variant={step1.nameIsDiry && !step1.name ? 'danger' : ''}
            maxLength='510'
          />
        </FormElement>
        <SpacingVertical />
        <Title variant={TITLE_VARIANT_S}>
          {intl.formatMessage({ id: 'Load.ABTestWizard.SelectMessageTitle' })} *
        </Title>
        <SpacingVertical variant={SPACING_8} />
        <Grid cols={5}>
          {step1.abTestMessages.map((item, key) => (
            <>
              <GridCell>
                <ThumbContainer>
                  <BoxThumbnail item={item} />
                </ThumbContainer>
              </GridCell>
            </>
          ))}
          {step1.abTestMessages.length < 2 && (
            <GridCell>
              <ThumbContainer>
                <Thumb>
                  <AddNewMessageBox enabled>
                    <Flex
                      justifyContent='center'
                      alignItems='center'
                      flex='0 1 auto'
                      width='100%'
                      height='100%'
                    >
                      <Button
                        kind={BUTTON_VARIANT_PRIMARY}
                        onClick={selectMessage}
                        data-mu-test='button-selectMessage-1'
                      >
                        {intl.formatMessage({
                          id: 'Load.ABTestWizard.ButtonSelectMessage'
                        })}
                      </Button>
                    </Flex>
                  </AddNewMessageBox>
                </Thumb>
              </ThumbContainer>
            </GridCell>
          )}
          {step1.abTestMessages.length < 1 && (
            <GridCell>
              <ThumbContainer>
                <Thumb>
                  <AddNewMessageBox enabled>
                    <Flex
                      justifyContent='center'
                      alignItems='center'
                      flex='0 1 auto'
                      width='100%'
                      height='100%'
                    >
                      <Button
                        kind={BUTTON_VARIANT_PRIMARY}
                        onClick={selectMessage}
                        data-mu-test='button-selectMessage-2'
                      >
                        {intl.formatMessage({
                          id: 'Load.ABTestWizard.ButtonSelectMessage'
                        })}
                      </Button>
                    </Flex>
                  </AddNewMessageBox>
                </Thumb>
              </ThumbContainer>
            </GridCell>
          )}
          {step1.abTestMessages.length < 2 && (
            <GridCell>
              <ThumbContainer>
                <Thumb>
                  <AddNewMessageBox enabled={false}>
                    <Flex
                      justifyContent='center'
                      alignItems='center'
                      flex='0 1 auto'
                      width='100%'
                      height='100%'
                    >
                      <Button
                        kind={BUTTON_VARIANT_PRIMARY}
                        disabled
                        data-mu-test='button-selectMessage-disabled'
                      >
                        {intl.formatMessage({
                          id: 'Load.ABTestWizard.ButtonSelectMessage'
                        })}
                      </Button>
                    </Flex>
                  </AddNewMessageBox>
                </Thumb>
              </ThumbContainer>
            </GridCell>
          )}
          {step1.abTestMessages.length < 5 && step1.abTestMessages.length >= 2 && (
            <GridCell>
              <ThumbContainer>
                <Thumb>
                  <AddNewMessageBox enabled isSelectable>
                    <Flex
                      justifyContent='center'
                      alignItems='center'
                      flex='0 1 auto'
                      width='100%'
                      height='100%'
                    >
                      <FlexItem margin='auto'>
                        <Button
                          kind={BUTTON_VARIANT_PRIMARY}
                          onClick={selectMessage}
                        >
                          {intl.formatMessage({
                            id: 'Load.ABTestWizard.ButtonSelectMessage'
                          })}
                        </Button>
                      </FlexItem>
                    </Flex>
                  </AddNewMessageBox>
                </Thumb>
              </ThumbContainer>
            </GridCell>
          )}
        </Grid>
        <SpacingVertical />
        <Title variant={TITLE_VARIANT_S}>
          {intl.formatMessage({
            id: 'Load.ABTestWizard.SelectPercentageTitle'
          })}
          {' *'}
        </Title>
        <SpacingVertical variant={SPACING_32} />
        <Tooltip id='handle-tooltip' delayUpdate={10} />
        <Slider
          value={step1.percTest}
          min={-2}
          max={102}
          onChange={onChangePercTest}
          marks={{ 0: 0, 20: 20, 40: 40, 60: 60, 80: 80, 100: 100 }}
          handleRender={handleRender}
        />
        <SpacingVertical />
      </Panel>
    </>
  )
}

export default BoxTestInfo
